import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X, Play, ChevronLeft, ChevronRight, Eye } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import { experiencesContent as c } from "../mock";

const ViewBadge = () => (
  <span className="absolute inset-0 flex items-center justify-center bg-brand-ink/0 group-hover:bg-brand-ink/25 transition-colors duration-500 pointer-events-none">
    <span className="opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500 inline-flex items-center gap-2 border border-ink-on-dark/80 text-ink-on-dark px-6 py-2.5 backdrop-blur-sm">
      <Eye className="w-4 h-4" />
      <span className="font-inter text-label-sm uppercase tracking-[0.2em]">View</span>
    </span>
  </span>
);

const RowArrow = ({ dir, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={dir < 0 ? "Scroll left" : "Scroll right"}
    data-testid={`gallery-arrow-${dir < 0 ? "prev" : "next"}`}
    className={`hidden md:flex absolute top-[42%] -translate-y-1/2 ${
      dir < 0 ? "-left-5" : "-right-5"
    } z-20 w-11 h-11 rounded-full border border-brand-gold bg-background-ivory/90 backdrop-blur-sm text-brand-ink hover:bg-brand-ink hover:text-ink-on-dark hover:border-brand-ink transition-colors items-center justify-center shadow-sm`}
  >
    {dir < 0 ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
  </button>
);

const Experiences = () => {
  const [lightbox, setLightbox] = useState(null);
  const [playWed, setPlayWed] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [showCraft, setShowCraft] = useState(false);
  const [showIntimate, setShowIntimate] = useState(false);
  const touchStartX = useRef(null);
  const intimateRef = useRef(null);
  const culturalRef = useRef(null);
  const scrollRow = (ref, dir) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  };

  const weddingArchive = [
    { video: "/circus.mp4", poster: "/circus-cover.jpg", caption: "The Circus" },
    { video: "/istanbul.mp4", poster: "/istanbul-cover.jpg", caption: "Istanbul, Turkey" },
    { image: "/gal-wed-grazing.webp", caption: "The Grazing Table" },
    { video: "/cannes.mp4", poster: "/cannes-cover.jpg", caption: "Monaco" },
    { image: "/gal-wed-banquet-hall.webp", caption: "The Banquet Hall" },
    { image: "/gal-wed-ballroom-dome.webp", caption: "A Canopy of Stars" },
  ];
  const intimateGallery = [
    { image: "/gal-buffet.webp", caption: "The Grand Buffet" },
    { video: "/candelabra.mp4", poster: "/candelabra-poster.jpg", caption: "Crystal & Candlelight" },
    { video: "/canapes.mp4", poster: "/canapes-cover.jpg", caption: "Skewered Canapés" },
  ];
  const culturalGallery = [
    { image: "/gal-cul-puchka.webp", caption: "The Gilded Puchka" },
    { image: "/gal-cul-kulfi.webp", caption: "Kulfi, Four Ways" },
  ];

  const openLightbox = (items, index) => setLightbox({ items, index });
  const navLightbox = useCallback((dir) => {
    setLightbox((lb) => {
      if (!lb) return lb;
      const n = lb.items.length;
      return { ...lb, index: (lb.index + dir + n) % n };
    });
  }, []);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      else if (e.key === "ArrowRight") navLightbox(1);
      else if (e.key === "ArrowLeft") navLightbox(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, navLightbox]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) navLightbox(dx < 0 ? 1 : -1);
    touchStartX.current = null;
  };
  return (
    <div className="min-h-screen flex flex-col bg-background-ivory">
      <Header />

      {/* Hero — subtle ivory */}
      <section className="pt-40 pb-24 px-margin-mobile md:px-margin-desktop">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal as="p" className="font-inter text-eyebrow text-brand-secondary uppercase mb-5">
            {c.chapter}
          </Reveal>
          <Reveal
            as="h1"
            delay={100}
            className="font-cormorant text-[24px] sm:text-[40px] md:text-[54px] leading-[1.1] text-brand-ink mb-8"
          >
            Creating<br />
            <span className="whitespace-nowrap">Experiences Beyond Catering</span>
          </Reveal>
          <Reveal delay={200} className="w-16 h-px bg-brand-gold mx-auto mb-8" />
          <Reveal
            as="p"
            delay={300}
            className="font-cormorant text-body-lg text-deep-taupe max-w-2xl mx-auto mb-10"
          >
            {c.heroSubtitle}
          </Reveal>
          <Reveal
            as="p"
            delay={400}
            className="font-inter text-eyebrow text-brand-secondary uppercase"
          >
            {c.caption}
          </Reveal>
        </div>
      </section>

      <div className="w-full h-px bg-hairline-beige" />

      {/* Alternating narrative sections with overlapping cards */}
      <section className="px-margin-mobile md:px-margin-desktop py-narrative-gap flex flex-col gap-narrative-gap">
        {c.sections.map((s, i) => {
          const imageLeft = s.align === "left";
          return (
            <React.Fragment key={s.title}>
            <div
              className="grid grid-cols-1 md:grid-cols-12 gap-gutter md:gap-16 items-center"
            >
              {/* Text */}
              <Reveal
                delay={120}
                className={`col-span-1 md:col-span-4 ${
                  imageLeft ? "md:order-2 md:col-start-9" : "md:order-1 md:col-start-2"
                }`}
              >
                <p className="font-inter text-eyebrow text-brand-secondary uppercase mb-4">
                  {s.eyebrow}
                </p>
                <h2 className="font-cormorant text-[26px] md:text-[33px] text-brand-ink mb-5 md:whitespace-nowrap">
                  {s.title}
                </h2>
                <p className="font-cormorant text-body-md text-deep-taupe mb-7">
                  {s.body}
                </p>
                {s.title === "Cultural Occasions" && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowCraft((v) => !v);
                      if (!showCraft) {
                        setTimeout(
                          () =>
                            document
                              .getElementById("cultural-gallery")
                              ?.scrollIntoView({ behavior: "smooth", block: "start" }),
                          120
                        );
                      }
                    }}
                    className="inline-flex items-center gap-3 border border-brand-gold px-7 py-3.5 text-brand-ink hover:bg-brand-ink hover:text-ink-on-dark hover:border-brand-ink transition-all duration-500"
                    data-testid="view-cultural-craft"
                  >
                    <span className="font-inter text-label-sm uppercase tracking-widest">
                      {showCraft ? "Hide the Craft" : s.cta}
                    </span>
                    <ArrowRight
                      className={`w-4 h-4 transition-transform duration-300 ${
                        showCraft ? "rotate-90" : ""
                      }`}
                    />
                  </button>
                )}
                {s.title === "Weddings" && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowArchive((v) => !v);
                      if (!showArchive) {
                        setTimeout(
                          () =>
                            document
                              .getElementById("wedding-archive")
                              ?.scrollIntoView({ behavior: "smooth", block: "start" }),
                          120
                        );
                      }
                    }}
                    className="inline-flex items-center gap-3 border border-brand-gold px-7 py-3.5 text-brand-ink hover:bg-brand-ink hover:text-ink-on-dark hover:border-brand-ink transition-all duration-500"
                    data-testid="view-wedding-archive"
                  >
                    <span className="font-inter text-label-sm uppercase tracking-widest">
                      {showArchive ? "Hide the Archive" : "View Our Wedding Archive"}
                    </span>
                    <ArrowRight
                      className={`w-4 h-4 transition-transform duration-300 ${
                        showArchive ? "rotate-90" : ""
                      }`}
                    />
                  </button>
                )}
              </Reveal>

              {/* Image (with optional overlap peek card) */}
              <Reveal
                className={`col-span-1 md:col-span-6 relative ${
                  imageLeft ? "md:order-1 md:col-start-1" : "md:order-2 md:col-start-6"
                }`}
              >
                {s.overlay && (
                  <div className="hidden md:block absolute -top-8 -right-6 w-1/2 aspect-[3/4] bg-cover-ivory border border-hairline-beige shadow-sm z-0 overflow-hidden">
                    <div className="w-full h-full flex items-end p-4">
                      <span className="font-cormorant text-body-md text-brand-ink">
                        {s.overlay}
                      </span>
                    </div>
                  </div>
                )}
                <div className="relative z-10 w-full aspect-[4/3] overflow-hidden group bg-cover-ivory">
                  {s.video ? (
                    playWed ? (
                      <video
                        className="w-full h-full object-cover"
                        autoPlay
                        muted
                        loop
                        playsInline
                        onLoadedMetadata={(e) => {
                          e.currentTarget.playbackRate = 0.6;
                        }}
                        data-testid="weddings-film"
                      >
                        <source src={s.video} type="video/mp4" />
                      </video>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setPlayWed(true)}
                        className="relative block w-full h-full group/play"
                        data-testid="weddings-play"
                      >
                        <img
                          src={s.poster}
                          alt={s.title}
                          className="w-full h-full object-cover"
                        />
                        <span className="absolute inset-0 bg-black/30 group-hover/play:bg-black/20 transition-colors" />
                        <span className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-ink-on-dark">
                          <span className="w-16 h-16 rounded-full border border-ink-on-dark/70 flex items-center justify-center backdrop-blur-sm transition-transform duration-500 group-hover/play:scale-110">
                            <Play className="w-6 h-6 ml-0.5" fill="currentColor" />
                          </span>
                          <span className="font-inter text-eyebrow uppercase tracking-widest">
                            Play the Film
                          </span>
                        </span>
                      </button>
                    )
                  ) : (
                    <img
                      src={s.image}
                      alt={s.title}
                      className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                    />
                  )}
                </div>
              </Reveal>
            </div>
            {s.title === "Weddings" && showArchive && (
              <div id="wedding-archive" data-testid="weddings-gallery-wrap" className="animate-fade-in">
                <p className="font-inter text-eyebrow text-brand-secondary uppercase tracking-widest text-center mb-8">
                  The Wedding Archive
                </p>
                <div
                  className="flex gap-gutter overflow-x-auto snap-x snap-mandatory pb-4"
                  data-testid="weddings-gallery"
                >
                  {weddingArchive.map((g, gi) => (
                    <Reveal
                      key={g.caption}
                      delay={gi * 80}
                      className="snap-start shrink-0 w-[240px] md:w-[300px] group"
                    >
                      {g.video ? (
                        <button
                          type="button"
                          onClick={() => openLightbox(weddingArchive, gi)}
                          className="block w-full text-left group/play"
                          data-testid={`wed-item-${gi}`}
                        >
                          <div className="relative w-full aspect-[3/4] overflow-hidden mb-4 bg-brand-ink">
                            <img
                              src={g.poster}
                              alt={g.caption}
                              className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                            />
                            <span className="absolute inset-0 bg-black/30 group-hover/play:bg-black/20 transition-colors" />
                            <span className="absolute inset-0 flex items-center justify-center text-ink-on-dark">
                              <span className="w-12 h-12 rounded-full border border-ink-on-dark/70 flex items-center justify-center backdrop-blur-sm transition-transform duration-500 group-hover/play:scale-110">
                                <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
                              </span>
                            </span>
                          </div>
                          <p className="font-inter text-eyebrow text-brand-secondary uppercase tracking-widest">
                            {g.caption}
                          </p>
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => openLightbox(weddingArchive, gi)}
                          className="block w-full text-left cursor-pointer"
                          data-testid={`wed-item-${gi}`}
                        >
                          <div className="w-full aspect-[3/4] overflow-hidden mb-4 bg-brand-ink">
                            <img
                              src={g.image}
                              alt={g.caption}
                              loading="lazy"
                              className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                            />
                          </div>
                          <p className="font-inter text-eyebrow text-brand-secondary uppercase tracking-widest">
                            {g.caption}
                          </p>
                        </button>
                      )}
                    </Reveal>
                  ))}
                </div>
              </div>
            )}
            {s.title === "Cultural Occasions" && showCraft && (
              <div id="cultural-gallery" data-testid="cultural-gallery-wrap" className="mt-2 animate-fade-in">
                <p className="font-inter text-eyebrow text-brand-secondary uppercase tracking-widest text-center mb-8">
                  Heritage, Reimagined
                </p>
                <div className="relative max-w-5xl mx-auto">
                  <RowArrow dir={-1} onClick={() => scrollRow(culturalRef, -1)} />
                  <RowArrow dir={1} onClick={() => scrollRow(culturalRef, 1)} />
                  <div
                    ref={culturalRef}
                    className="flex gap-gutter overflow-x-auto snap-x snap-mandatory pb-2"
                  >
                  {culturalGallery.map((g, ci) => (
                    <Reveal key={g.caption} delay={ci * 120} className="group snap-start shrink-0 w-[85%] sm:w-[60%] md:w-[48%]">
                      <button
                        type="button"
                        onClick={() => openLightbox(culturalGallery, ci)}
                        className="block w-full text-left cursor-pointer"
                        data-testid={`cultural-item-${ci}`}
                      >
                        <div className="relative w-full aspect-[4/3] overflow-hidden mb-4 bg-brand-ink">
                          <img
                            src={g.image}
                            alt={g.caption}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                          />
                          <ViewBadge />
                        </div>
                        <p className="font-inter text-eyebrow text-brand-secondary uppercase tracking-widest">
                          {g.caption}
                        </p>
                      </button>
                    </Reveal>
                  ))}
                  </div>
                </div>
              </div>
            )}
            </React.Fragment>
          );
        })}
      </section>

      {/* Intimate Gatherings — two dish cards */}
      <section className="bg-cover-ivory px-margin-mobile md:px-margin-desktop py-narrative-gap">
        <div className="max-w-2xl mx-auto text-center mb-16">
          <Reveal
            as="h2"
            className="font-cormorant text-[33px] md:text-[45px] text-brand-ink mb-6"
          >
            {c.intimate.title}
          </Reveal>
          <Reveal as="p" delay={120} className="font-cormorant text-body-md text-deep-taupe">
            {c.intimate.subtitle}
          </Reveal>
          <Reveal delay={220} className="mt-10">
            <button
              type="button"
              onClick={() => {
                setShowIntimate((v) => !v);
                if (!showIntimate) {
                  setTimeout(
                    () =>
                      document
                        .getElementById("intimate-gallery-wrap")
                        ?.scrollIntoView({ behavior: "smooth", block: "start" }),
                    120
                  );
                }
              }}
              className="inline-flex items-center gap-3 border border-brand-gold px-7 py-3.5 text-brand-ink hover:bg-brand-ink hover:text-ink-on-dark hover:border-brand-ink transition-all duration-500"
              data-testid="view-intimate-gallery"
            >
              <span className="font-inter text-label-sm uppercase tracking-widest">
                {showIntimate ? "Hide the Gallery" : "View the Gallery"}
              </span>
              <ArrowRight
                className={`w-4 h-4 transition-transform duration-300 ${
                  showIntimate ? "rotate-90" : ""
                }`}
              />
            </button>
          </Reveal>
        </div>

        {showIntimate && (
        <div id="intimate-gallery-wrap" className="relative animate-fade-in">
          <RowArrow dir={-1} onClick={() => scrollRow(intimateRef, -1)} />
          <RowArrow dir={1} onClick={() => scrollRow(intimateRef, 1)} />
          <div
            ref={intimateRef}
            className="flex gap-gutter overflow-x-auto snap-x snap-mandatory pb-4"
            data-testid="intimate-gallery"
          >
          {intimateGallery.map((g, i) => (
            <Reveal
              key={g.caption}
              delay={i * 90}
              className="snap-start shrink-0 w-[240px] md:w-[300px] group"
            >
              {g.video ? (
                <button
                  type="button"
                  onClick={() => openLightbox(intimateGallery, i)}
                  className="block w-full text-left group/play"
                  data-testid={`gallery-item-${i}`}
                >
                  <div className="relative w-full aspect-[3/4] overflow-hidden mb-4 bg-brand-ink">
                    <img
                      src={g.poster}
                      alt={g.caption}
                      className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                    />
                    <span className="absolute inset-0 bg-black/30 group-hover/play:bg-black/20 transition-colors" />
                    <span className="absolute inset-0 flex items-center justify-center text-ink-on-dark">
                      <span className="w-12 h-12 rounded-full border border-ink-on-dark/70 flex items-center justify-center backdrop-blur-sm transition-transform duration-500 group-hover/play:scale-110">
                        <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
                      </span>
                    </span>
                  </div>
                  <p className="font-inter text-eyebrow text-brand-secondary uppercase tracking-widest">
                    {g.caption}
                  </p>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => openLightbox(intimateGallery, i)}
                  className="block w-full text-left cursor-pointer"
                  data-testid={`gallery-item-${i}`}
                >
                  <div className="relative w-full aspect-[3/4] overflow-hidden mb-4 bg-brand-ink">
                    <img
                      src={g.image}
                      alt={g.caption}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                    />
                    <ViewBadge />
                  </div>
                  <p className="font-inter text-eyebrow text-brand-secondary uppercase tracking-widest">
                    {g.caption}
                  </p>
                </button>
              )}
            </Reveal>
          ))}
          </div>
        </div>
        )}
      </section>

      {/* CTA */}
      <section className="px-margin-mobile md:px-margin-desktop py-narrative-gap">
        <div className="max-w-2xl mx-auto text-center">
          <Reveal
            as="h2"
            className="font-cormorant text-[33px] md:text-[45px] text-brand-ink mb-10"
          >
            {c.cta.title}
          </Reveal>
          <Reveal delay={150}>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 border border-brand-gold px-8 py-4 text-brand-ink hover:bg-brand-ink hover:text-ink-on-dark hover:border-brand-ink transition-all duration-500"
            >
              <span className="font-inter text-label-sm uppercase tracking-widest">
                {c.cta.button}
              </span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {lightbox && (() => {
        const current = lightbox.items[lightbox.index];
        const multiple = lightbox.items.length > 1;
        return (
          <div
            className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-6 animate-fade-in"
            onClick={() => setLightbox(null)}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            data-testid="gallery-lightbox"
          >
            <button
              onClick={() => setLightbox(null)}
              className="absolute top-6 right-6 text-ink-on-dark/70 hover:text-ink-on-dark transition-colors z-20"
              data-testid="lightbox-close"
              aria-label="Close"
            >
              <X className="w-7 h-7" />
            </button>
            {multiple && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navLightbox(-1);
                  }}
                  className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-ink-on-dark/40 text-ink-on-dark/70 hover:text-ink-on-dark hover:border-ink-on-dark/80 flex items-center justify-center backdrop-blur-sm transition-colors z-20"
                  data-testid="lightbox-prev"
                  aria-label="Previous"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navLightbox(1);
                  }}
                  className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-ink-on-dark/40 text-ink-on-dark/70 hover:text-ink-on-dark hover:border-ink-on-dark/80 flex items-center justify-center backdrop-blur-sm transition-colors z-20"
                  data-testid="lightbox-next"
                  aria-label="Next"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
            <figure
              className="flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              {current.video ? (
                <video
                  key={current.video}
                  className="max-w-[92vw] max-h-[80vh] object-contain shadow-2xl bg-black"
                  autoPlay
                  controls
                  loop
                  playsInline
                  poster={current.poster}
                  data-testid="lightbox-video"
                >
                  <source src={current.video} type="video/mp4" />
                </video>
              ) : (
                <img
                  key={current.image}
                  src={current.image}
                  alt={current.caption}
                  className="max-w-[92vw] max-h-[80vh] object-contain shadow-2xl"
                />
              )}
              <figcaption className="font-inter text-eyebrow uppercase tracking-widest text-ink-on-dark/70 mt-5">
                {current.caption}
              </figcaption>
            </figure>
          </div>
        );
      })()}

      <Footer />
    </div>
  );
};

export default Experiences;
